import { Component, OnInit } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, Validators, UntypedFormGroup } from '@angular/forms';
import { Haulage } from '../models/Haulage';
import { Fleet } from '../models/Fleet';
import { User } from '../models';
import { FleetService } from '../services/fleet.service';
import { ApiResponse } from '../models/ApiResponse';
import { Observable, forkJoin } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

class RawEntry {
    content: string;
    valid: boolean = false;
    processed: boolean = false;
    error: string = null;
}

@Component({
    selector: 'admin',
    templateUrl: './admin.component.html',
    styleUrls: ['admin.component.scss'],
    standalone: false
})
export class AdminComponent implements OnInit {
    constructor(private formBuilder: UntypedFormBuilder, private fleetService: FleetService) { }

    form: UntypedFormGroup;
    fileControl: UntypedFormControl;
    ptoControl: UntypedFormControl;
    fleetTypeControl: UntypedFormControl;

    rawEntries: RawEntry[] = [];
    processing: boolean = false;
    duplicateGroups: String[] = [];

    ngOnInit() {
        this.fileControl = new UntypedFormControl('', Validators.required);
        this.ptoControl = new UntypedFormControl('', Validators.required);
        this.fleetTypeControl = new UntypedFormControl('', Validators.required);
        this.form = this.formBuilder.group({
            file: this.fileControl,
            pto: this.ptoControl,
            fleetType: this.fleetTypeControl
        });
    }

    fileChange($event: any) {
        if ($event.target.files && $event.target.files[0]) {
            this.rawEntries = [];
            this.duplicateGroups = [];

            const file = $event.target.files[0];
            const reader = new FileReader();
            const haulageList: Haulage[] = [];
            reader.onload = () => {
                const csvData = reader.result;
                const splitData = (<string>csvData).split(/\r\n|\n/);
                let entryAmount = splitData.length;
                this.rawEntries = [];
                this.processing = true;
                splitData.forEach(async s => {
                    // Just in case the file ends with an empty newline
                    if (s === '') {
                        entryAmount = entryAmount - 1;
                        return;
                    }

                    const entry = new RawEntry();
                    entry.content = s.trim();
                    entry.valid = await this.verifyEntry(entry.content);

                    if (!entry.valid) {
                        this.form.controls['file'].setErrors({
                            invalidEntries: 'Some entries did not parse validation'
                        });
                    }

                    // This was the only way I could figure out to make the spinner not disappear *immediately*, and only
                    // after all the entries were processed.
                    this.rawEntries.push(entry);
                    if (this.rawEntries.length == entryAmount) {
                        this.processing = false;
                    }
                });
            };

            reader.onerror = (e) => {
                console.log(e);
            };

            reader.readAsText(file);
        }
    }

    fileInputCanOpen(event: any) {
        if (this.form.controls['pto'].value !== '' && this.form.controls['fleetType'].value !== '') {
            return true;
        } else {
            event.preventDefault();
            event.stopPropagation();
            return false;
        }
    }

    async importData() {
        this.processing = true;

        for (let i = 0; i < this.rawEntries.length; i++) {
            const entry = this.rawEntries[i];
            const haulage = this.csvStringToHaulage(entry.content);

            try {
                await this.fleetService.createFleet(haulage);
                entry.processed = true;
            } catch (response) {
                entry.processed = true;
                entry.error = response.error.errors.join('\n');
            }
        }

        this.processing = false;
    }

    async verifyEntry(entry: string): Promise<boolean> {
        // RegEx explanation:
        // ^ - start of line
        // \d+, - Digits only, until a comma
        // .+?, (3 times) - Any character 1 or more times, matched lazily until a comma
        // .+?@.+\.[^,]+$ - For emails.
        // .+?            - Any character 1 or more times
        //    @           - Literal '@'
        //     .+\.       - Any character 1 or more times, and a '.' literal
        //         [^,]+$ - Any non-comma character, 1 or more times, until end of line
        // In essence, just enforcing the format of the CSV, as well as the number of columns in it.
        const pattern = /^\d+,.+?,.+?,.+?,.+?\@.+\.[^,]+$/;

        const asHaulage: Haulage = this.csvStringToHaulage(entry);
        console.log(`Querying if fleetname ${asHaulage.fleet.name} already exists`);
        const precheckResult = await this.fleetService.fleetPrecheck(asHaulage);
        console.log(`Response from API: ${precheckResult.message}`);
        // JSON.parse fails on an empty string
        const parsedMessage = precheckResult.message !== '' ? JSON.parse(precheckResult.message) : '';
        if (parsedMessage !== '') {
            this.duplicateGroups.push(parsedMessage);
        }

        return entry.match(pattern) != null && precheckResult.message === '';
    }

    csvStringToHaulage(csvString: String) {
        const entry = csvString;
        const parts = entry.split(',');
        const fleet = new Fleet();
        fleet.name = parts[1].trim();
        fleet.pto = this.form.controls['pto'].value;
        fleet.fleetType = this.form.controls['fleetType'].value;

        const user = new User();
        const userNameParts = parts[3].trim().split(' ');
        user.firstName = userNameParts[0];
        user.lastName = userNameParts.slice(1, userNameParts.length).join();
        user.email = parts[4].trim();

        const haulage = new Haulage();
        haulage.fleet = fleet;
        haulage.cvr = parts[0].trim();
        haulage.companyName = parts[2].trim();
        haulage.users.push(user);

        return haulage;
    }

    getHeaderArray(csvRecordsArr: any) {
        const headers = (<string>csvRecordsArr[0]).split(',');
        const headerArray = [];
        for (let j = 0; j < headers.length; j++) {
            headerArray.push(headers[j]);
        }
        return headerArray;
    }
}
